import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { updatePart } from '../../redux/slices/partsSlice';
import { useLanguage } from '../../context/LanguageContext';

const buildForm = (data) => ({
  name: data.name || '',
  description: data.description || '',
  color: data.color || '',
  category: data.category || '',
  supplier: data.supplier || '',
  sku: data.sku || '',
  article_id: data.article_id || '',
  purchase_price: data.purchase_price ?? '',
  selling_price: data.selling_price ?? '',
  quantity: data.quantity ?? '',
  min_stock_level: data.min_stock_level ?? '',
});

const PartDetails = ({ data, onRefetch, onClose, startInEdit = false }) => {
  const dispatch = useDispatch();
  const { t, language } = useLanguage();
  const dateLocale = language === 'de' ? 'de-DE' : 'en-GB';
  const [isEditing, setIsEditing] = useState(startInEdit);
  const [form, setForm] = useState(startInEdit ? buildForm(data) : null);

  const getStatusColor = (status) => {
    const statusColors = {
      in_stock: 'bg-green-100 text-green-800',
      low_stock: 'bg-yellow-100 text-yellow-800',
      out_of_stock: 'bg-red-100 text-red-800',
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const startEdit = () => {
    setForm(buildForm(data));
    setIsEditing(true);
  };

  const handleSave = (e) => {
    e.preventDefault();
    dispatch(updatePart({ id: data.id, data: form }))
      .unwrap()
      .then(() => {
        setIsEditing(false);
        onRefetch?.();
      })
      .catch((err) => console.error('Failed to save part:', err));
  };

  if (isEditing) {
    return (
      <form onSubmit={handleSave} className='space-y-4'>
        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>{t('partDetails.partNameRequired')}</label>
            <input
              type='text'
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className='input-field'
              required
            />
          </div>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>{t('partDetails.skuOrderNumber')}</label>
            <input
              type='text'
              value={form.sku}
              onChange={(e) => setForm({ ...form, sku: e.target.value })}
              className='input-field'
            />
          </div>
        </div>
        <div className='grid grid-cols-3 gap-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>{t('partDetails.colorLabel')}</label>
            <input
              type='text'
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              className='input-field'
            />
          </div>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>{t('common.category')}</label>
            <input
              type='text'
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className='input-field'
            />
          </div>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>{t('partDetails.supplierLabel')}</label>
            <input
              type='text'
              value={form.supplier}
              onChange={(e) => setForm({ ...form, supplier: e.target.value })}
              className='input-field'
            />
          </div>
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>{t('partDetails.articleId')}</label>
          <input
            type='text'
            value={form.article_id}
            onChange={(e) => setForm({ ...form, article_id: e.target.value })}
            className='input-field'
          />
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>{t('common.description')}</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className='input-field'
            rows='2'
          />
        </div>
        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>{t('partDetails.purchasePrice')}</label>
            <input
              type='number'
              step='0.01'
              min='0'
              value={form.purchase_price}
              onChange={(e) => setForm({ ...form, purchase_price: e.target.value })}
              className='input-field'
            />
          </div>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>{t('partDetails.sellingPrice')}</label>
            <input
              type='number'
              step='0.01'
              min='0'
              value={form.selling_price}
              onChange={(e) => setForm({ ...form, selling_price: e.target.value })}
              className='input-field'
            />
          </div>
        </div>
        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>{t('common.quantity')}</label>
            <input
              type='number'
              min='0'
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              className='input-field'
            />
          </div>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>{t('partDetails.minStockLevel')}</label>
            <input
              type='number'
              min='0'
              value={form.min_stock_level}
              onChange={(e) => setForm({ ...form, min_stock_level: e.target.value })}
              className='input-field'
            />
          </div>
        </div>
        <div className='flex justify-end space-x-3 pt-2'>
          <button type='button' onClick={() => setIsEditing(false)} className='btn-secondary'>
            {t('common.cancel')}
          </button>
          <button type='submit' className='btn-primary'>
            {t('partDetails.saveChanges')}
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='bg-gray-50 rounded-lg p-4'>
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-lg font-semibold text-gray-900'>
            {t('partDetails.partInformation')}
          </h3>
          <button onClick={startEdit} className='btn-secondary text-sm'>
            {t('common.edit')}
          </button>
        </div>
        <div className='grid grid-cols-2 gap-4'>
          <div>
            <p className='text-sm text-gray-500'>{t('common.name')}</p>
            <p className='font-medium text-gray-900'>{data.name}</p>
          </div>
          <div>
            <p className='text-sm text-gray-500'>{t('common.status')}</p>
            <span
              className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(data.status)}`}
            >
              {data.status?.toUpperCase() || t('partDetails.na')}
            </span>
            {!!data.is_nla && (
              <span className='inline-block ml-2 px-2 py-1 text-xs font-medium rounded-full bg-gray-700 text-white'>
                {t('partsTable.nla')}
              </span>
            )}
          </div>
          <div>
            <p className='text-sm text-gray-500'>{t('common.category')}</p>
            {data.category ? (
              <button className='font-medium text-blue-600 hover:text-blue-800 hover:underline'>
                {data.category}
              </button>
            ) : (
              <p className='text-gray-400'>-</p>
            )}
          </div>
          <div>
            <p className='text-sm text-gray-500'>{t('partDetails.createdLabel')}</p>
            <p className='font-medium text-gray-900'>
              {data.created_at
                ? new Date(data.created_at).toLocaleDateString(dateLocale)
                : '-'}
            </p>
          </div>
        </div>
        {data.description && (
          <div className='mt-4'>
            <p className='text-sm text-gray-500'>{t('common.description')}</p>
            <p className='text-gray-700'>{data.description}</p>
          </div>
        )}
      </div>

      <div className='grid grid-cols-3 gap-4'>
        <div className='bg-green-50 rounded-lg p-4 text-center'>
          <p className='text-sm text-green-600'>{t('partDetails.totalStock')}</p>
          <p className='text-2xl font-bold text-green-700'>{data?.quantity}</p>
        </div>
        <div className='bg-purple-50 rounded-lg p-4 text-center'>
          <p className='text-sm text-purple-600'>{t('partDetails.totalValue')}</p>
          <p className='text-2xl font-bold text-purple-700'>
            CHF {(data?.quantity * data?.purchase_price).toFixed(2)}
          </p>
        </div>
      </div>

      <div>
        {!data ? (
          <div className='text-center py-8 text-gray-500'>
            {t('partDetails.loadingPartDetails')}
          </div>
        ) : data?.length === 0 ? (
          <div className='text-center py-8 bg-gray-50 rounded-lg'>
            <p className='text-gray-500'>{t('partDetails.noColorsAssigned')}</p>
          </div>
        ) : (
          <div className='border rounded-lg overflow-hidden'>
            <table className='min-w-full divide-y divide-gray-200'>
              <thead className='bg-gray-50'>
                <tr>
                  <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>
                    {t('partDetails.colorLabel')}
                  </th>
                  <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>
                    {t('partDetails.purchaseCol')}
                  </th>
                  <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>
                    {t('partDetails.sellingCol')}
                  </th>
                  <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>
                    {t('partDetails.orderNumberCol')}
                  </th>
                  <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>
                    {t('partDetails.supplierLabel')}
                  </th>
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-gray-200'>
                {data ? (
                  <tr key={data.id} className='hover:bg-gray-50 '>
                    <td className='px-4 py-3 text-sm font-medium text-blue-600 hover:text-blue-800'>
                      {data.color}
                    </td>
                    <td className='px-4 py-3 text-sm text-gray-900'>
                      CHF {parseFloat(data.purchase_price || 0).toFixed(2)}
                    </td>
                    <td className='px-4 py-3 text-sm text-green-600'>
                      CHF {parseFloat(data.selling_price || 0).toFixed(2)}
                    </td>
                    <td className='px-4 py-3 text-sm font-semibold'>
                      {data.sku || 0}
                    </td>
                    <td className='px-4 py-3'>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(data.stock_status)}`}
                      >
                        {data.supplier || 'N/A'}
                      </span>
                    </td>
                  </tr>
                ) : (
                  'Loading'
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartDetails;
